import {rewriteUrlForLocale} from '@common/custom-page/use-localized-url';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {useMutation} from '@tanstack/react-query';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {Localization} from '@ui/i18n/localization';
import {useNavigate} from 'react-router';

interface ChangeLocaleResponse extends BackendResponse {
  locale: Localization;
}

export function useChangeLocale() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (props: {locale?: string}) =>
      apiClient
        .post<ChangeLocaleResponse>(`users/me/locale`, props)
        .then(r => r.data),
    onSuccess: response => {
      // merge the freshly-fetched translation lines for the selected locale
      // into the bootstrap locales list. The initial bootstrap only ships
      // lines for the originally-active locale, so without this the switched
      // locale would have empty lines and all <Trans> strings would fall back
      // to English until a full page reload.
      const locales = getBootstrapData().i18n.locales.map(locale =>
        locale.language === response.locale.language
          ? {...locale, lines: response.locale.lines ?? locale.lines}
          : locale,
      );
      mergeBootstrapData({
        i18n: {
          locales,
          active: response.locale.language,
        },
      });
      // Rewrite the URL so the locale segment + slug both flip to the new
      // language (e.g. /de/kontakt → /en/contact). In-app surfaces without
      // a locale prefix (/drive, /admin/...) are left alone.
      const newPath = rewriteUrlForLocale(
        window.location.pathname,
        response.locale.language,
      );
      if (newPath !== window.location.pathname) {
        navigate(`${newPath}${window.location.search}${window.location.hash}`, {
          replace: true,
        });
      }
    },
    onError: err => showHttpErrorToast(err),
  });
}
